import type { ReactNode } from 'react';
import { cn } from './cn';

export interface PageHeaderProps {
  /** Page title (h1). */
  title: ReactNode;
  /** Optional one-line supporting description under the title. */
  subtitle?: ReactNode;
  /** Optional eyebrow/label above the title (e.g. a section name). */
  eyebrow?: ReactNode;
  /** Right-aligned actions (buttons, filters). Wrap on small screens. */
  actions?: ReactNode;
  /** Optional leading icon chip. */
  icon?: ReactNode;
  className?: string;
}

/**
 * The one page-title primitive. The audit found ~5 different heading treatments
 * (antd Typography.Title level 4, `h1 text-xl`, `h2 text-lg`, `text-[22px]`…) — this
 * unifies them: a semantic `<h1>`, consistent size/spacing, and a responsive actions slot.
 */
export function PageHeader({ title, subtitle, eyebrow, actions, icon, className }: PageHeaderProps) {
  return (
    <div className={cn('mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between', className)}>
      <div className="flex min-w-0 items-start gap-3">
        {icon && (
          <span className="mt-0.5 grid size-10 shrink-0 place-items-center rounded-card bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300 [&>svg]:size-5">
            {icon}
          </span>
        )}
        <div className="min-w-0">
          {eyebrow && (
            <div className="mb-1 text-[12px] font-bold uppercase tracking-[0.12em] text-brand-600 dark:text-brand-400">
              {eyebrow}
            </div>
          )}
          <h1 className="truncate text-xl font-semibold tracking-tight text-content sm:text-2xl">{title}</h1>
          {subtitle && <p className="mt-1 text-sm leading-relaxed text-muted">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export interface PageContainerProps {
  children: ReactNode;
  className?: string;
  /** Cap the content width for readability on very large screens (default full). */
  width?: 'full' | 'wide' | 'narrow';
}

const WIDTH: Record<NonNullable<PageContainerProps['width']>, string> = {
  full: 'max-w-none',
  wide: 'max-w-[1400px]',
  narrow: 'max-w-[880px]',
};

/** Consistent page content wrapper (max-width + vertical rhythm). The shell already
 *  supplies outer page padding, so this only handles width + inter-section spacing. */
export function PageContainer({ children, className, width = 'full' }: PageContainerProps) {
  return <div className={cn('mx-auto w-full', WIDTH[width], className)}>{children}</div>;
}

export interface SectionHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

/** Smaller heading for a section within a page (h2). */
export function SectionHeader({ title, description, actions, className }: SectionHeaderProps) {
  return (
    <div className={cn('mb-3 flex items-end justify-between gap-3', className)}>
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-content">{title}</h2>
        {description && <p className="mt-0.5 text-[13px] text-muted">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
